import React from 'react';

import { contactStrings } from '@data/contacts';
import { useIsAdmin } from '@hooks/useIsAdmin';
import type { ContactForm, ContactType } from '@models/Contact';
import { ClassMap, makeMuiStyles, useCommonStyles } from '@styles/index';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, common) => ({
  contactDisplay: {
    ...common.smallerText,
    ...common.textGray900,
    ...common.flexColumn,
    rowGap: 4,
    '& > div': {
      wordBreak: 'break-all',
    },
  },
}));

export const BookingInquiryContactDisplay: React.FC<{
  contact: Omit<ContactForm, 'user'> | null | undefined;
  contactType: ContactType;
  style?: React.CSSProperties;
  classes?: ClassMap<'contactDisplay'>;
}> = ({ contact, contactType, style, classes: customClasses }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const showAddress1CN =
    useIsAdmin() && (contactType === 'pickup_address' || contactType === 'shipper');

  const showSocialCreditId = contactType === 'shipper';

  if (!contact) return <>-</>;

  return (
    <div className={clsx(classes.contactDisplay, customClasses?.contactDisplay)} style={style}>
      <div>
        {contact.company_name || (
          <span className={classes.textGray400}>{contactStrings.company_name}...</span>
        )}
      </div>

      <div className={classes.textGray500}>
        {contact.english_name || (
          <span className={classes.textGray400}>{contactStrings.english_name}...</span>
        )}
      </div>

      {showSocialCreditId && (
        <div>
          {contact.social_credit_id || (
            <span className={classes.textGray400}>{contactStrings.social_credit_id}...</span>
          )}
        </div>
      )}

      <div>
        {'〒'}
        {contact.zip_code || (
          <span className={classes.textGray400}>{contactStrings.zip_code}...</span>
        )}
      </div>

      {showAddress1CN && (
        <div>
          {contact.address1_cn || (
            <span className={classes.textGray600}>{contactStrings.address1_cn}...</span>
          )}{' '}
          {contact.address1 ? (
            <span className={classes.textGray400}>{contact.address1}</span>
          ) : (
            <span className={classes.textGray600}>{contactStrings.address1}...</span>
          )}
        </div>
      )}

      {!showAddress1CN && (
        <div>
          {contact.address1 || (
            <span className={classes.textGray400}>{contactStrings.address1}...</span>
          )}
        </div>
      )}

      <div>
        {contact.address2 || (
          <span className={classes.textGray400}>{contactStrings.address2}...</span>
        )}
      </div>

      <div>
        {contact.principal_name || (
          <span className={classes.textGray400}>{contactStrings.principal_name}...</span>
        )}
      </div>

      <div>
        {contact.phone || <span className={classes.textGray400}>{contactStrings.phone}...</span>}
      </div>

      <div>
        {contact.email || <span className={classes.textGray400}>{contactStrings.email}...</span>}
      </div>

      <div>
        {contact.sns_id || <span className={classes.textGray400}>{contactStrings.sns_id}...</span>}
      </div>
    </div>
  );
};
